'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { X, User, Phone, Mail, Users, CreditCard, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import type { Trip } from '@/lib/mockData';

interface BookingFormData {
  name: string;
  phone: string;
  email: string;
  seats: number;
  notes: string;
}

interface BookingModalProps {
  trip: Trip;
  onClose: () => void;
}

export default function BookingModal({ trip, onClose }: BookingModalProps) {
  const [loading, setLoading] = useState(false);
  const [booked, setBooked] = useState(false);
  const seatsLeft = trip.totalSeats - trip.bookedSeats;
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<BookingFormData>({ defaultValues: { seats: 1 } });

  const seats = watch('seats') || 1;
  const total = trip.price * seats;

  const onSubmit = async (data: BookingFormData) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1800));
    setLoading(false);
    setBooked(true);
    toast.success(`Booking confirmed for ${data.name}! Check your email for details.`);

    const bookingId = `BK-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 90000)}`;
    const paymentId = `PAY-RZPY-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
    const bookingDate = new Date().toISOString().split('T')[0];

    const params = new URLSearchParams({
      bookingId,
      tripTitle: trip.title,
      tripLocation: `${trip.location}, ${trip.state}`,
      tripDuration: String(trip.duration),
      travelDate: trip.startDate,
      customerName: data.name,
      customerEmail: data.email,
      customerPhone: data.phone,
      seats: String(data.seats),
      totalAmount: String(trip.price * data.seats),
      paymentId,
      bookingDate,
    });

    setTimeout(() => {
      router.push(`/booking-confirmation?${params.toString()}`);
    }, 1200);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl shadow-black/30 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Premium gradient top bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#E8440A] via-[#ff6b35] to-[#E8440A] rounded-t-2xl" />

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-[#1A3C5E] flex items-center gap-2">
              <Sparkles size={16} className="text-[#E8440A]" />
              Book Your Spot
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{trip.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all hover:scale-110"
          >
            <X size={18} />
          </button>
        </div>

        {booked ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-300">
              <CheckCircle size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Booking Confirmed!</h3>
            <p className="text-gray-500 text-sm mb-6">
              Your booking for <strong>{trip.title}</strong> has been received. Our team will contact you within 24 hours to confirm your spot.
            </p>
            <button
              onClick={onClose}
              className="relative w-full overflow-hidden group flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-bold text-base text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-orange-400/40"
              style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #E8440A 40%, #c73508 100%)' }}
            >
              <span className="absolute inset-0 bg-gradient-to-b from-white/25 via-white/5 to-transparent pointer-events-none rounded-xl" />
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none" />
              <span className="relative">Done</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            {/* Trip Summary */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-xl p-4 mb-6">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Trip Date</span>
                <span className="font-semibold text-gray-800">
                  {new Date(trip.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Duration</span>
                <span className="font-semibold text-gray-800">{trip.duration} days</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Seats Available</span>
                <span className="font-semibold text-emerald-600">{seatsLeft} remaining</span>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {/* Name */}
              <div>
                <label className="label-field" htmlFor="booking-name">Full Name</label>
                <div className="relative">
                  <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="booking-name"
                    type="text"
                    placeholder="Arjun Mehta"
                    className={`input-field pl-10 ${errors.name ? 'border-red-400 focus:ring-red-200' : ''}`}
                    {...register('name', { required: 'Full name is required', minLength: { value: 2, message: 'Name too short' } })}
                  />
                </div>
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="label-field" htmlFor="booking-phone">Phone Number</label>
                <div className="relative">
                  <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="booking-phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    className={`input-field pl-10 ${errors.phone ? 'border-red-400 focus:ring-red-200' : ''}`}
                    {...register('phone', {
                      required: 'Phone number is required',
                      pattern: { value: /^[+\d\s]{10,14}$/, message: 'Enter a valid phone number' },
                    })}
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="label-field" htmlFor="booking-email">Email Address</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="booking-email"
                    type="email"
                    placeholder="you@example.com"
                    className={`input-field pl-10 ${errors.email ? 'border-red-400 focus:ring-red-200' : ''}`}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
                    })}
                  />
                </div>
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              </div>

              {/* Seats */}
              <div>
                <label className="label-field" htmlFor="booking-seats">Number of Seats</label>
                <div className="relative">
                  <Users size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    id="booking-seats"
                    className="input-field pl-10 appearance-none cursor-pointer"
                    {...register('seats', { required: true, min: 1, max: Math.min(seatsLeft, 10) })}
                  >
                    {Array.from({ length: Math.min(seatsLeft, 10) }).map((_, i) => (
                      <option key={`seat-opt-${i + 1}`} value={i + 1}>{i + 1} seat{i > 0 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="label-field" htmlFor="booking-notes">Special Requests (optional)</label>
                <textarea
                  id="booking-notes"
                  rows={2}
                  placeholder="Dietary requirements, medical conditions, special requests..."
                  className="input-field resize-none"
                  {...register('notes')}
                />
              </div>
            </div>

            {/* Price Summary */}
            <div className="mt-5 pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-gray-500">₹{trip.price.toLocaleString('en-IN')} × {seats} seat{seats > 1 ? 's' : ''}</span>
                <span className="font-semibold">₹{total.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm mb-3">
                <span className="text-gray-500">Taxes & fees</span>
                <span className="text-emerald-600 font-medium text-xs">Included</span>
              </div>
              <div className="flex justify-between font-bold text-base border-t border-gray-100 pt-2">
                <span className="text-gray-800">Total Amount</span>
                <span className="bg-gradient-to-r from-[#E8440A] to-[#c73508] bg-clip-text text-transparent font-mono">₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* Premium shiny pay button */}
            <button
              type="submit"
              disabled={loading}
              className="relative w-full overflow-hidden group flex items-center justify-center gap-2 mt-5 py-3.5 px-6 rounded-xl font-bold text-base text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-orange-400/40 hover:shadow-xl hover:shadow-orange-400/50 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #E8440A 40%, #c73508 100%)' }}
            >
              {/* Gloss overlay */}
              <span className="absolute inset-0 bg-gradient-to-b from-white/25 via-white/5 to-transparent pointer-events-none rounded-xl" />
              {/* Shimmer sweep */}
              {!loading && (
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none" />
              )}
              <span className="relative flex items-center gap-2">
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <CreditCard size={18} />
                    Pay ₹{total.toLocaleString('en-IN')} via Razorpay
                  </>
                )}
              </span>
            </button>
            <p className="text-center text-xs text-gray-400 mt-2">
              🔒 By booking, you agree to our cancellation policy and terms of service.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}